// Snippets bar for the Query page — the cloud counterpart of the
// desktop snippets library (insert a saved query into the editor,
// save the current editor text as a snippet, delete one). Snippet
// CRUD requires ManageDashboards; everyone with query access can
// insert.

import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { canManageDashboards, useMe } from '@/lib/useMe';
import { createSnippet, deleteSnippet, patchSnippet, useSnippets } from '@/lib/useSnippets';
import type { SnippetDef } from '@/lib/useSnippets';
import { cn } from '@/lib/utils';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

export function SnippetsBar({
  currentText,
  onInsert,
}: {
  currentText: string;
  onInsert: (content: string) => void;
}) {
  const auth = useAuth();
  const me = useMe();
  const canManage = canManageDashboards(me.data);
  const queryClient = useQueryClient();
  const snippets = useSnippets();
  const [selectedId, setSelectedId] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [editing, setEditing] = useState<SnippetDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = snippets.data?.snippets ?? [];
  const selected = list.find((s) => s.id === selectedId);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['snippets'] });

  const handleDelete = async () => {
    const token = auth.user?.id_token;
    if (!token || !selected) return;
    if (!window.confirm(`Delete snippet "${selected.name}"?`)) return;
    setBusy(true);
    setError(null);
    const r = await deleteSnippet(selected.id, token);
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setSelectedId('');
    refresh();
  };

  // Snippets are 503-disabled when the deployment has no state layer;
  // hide the bar entirely rather than showing a dead control.
  if (snippets.error) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-tres-text-secondary">
        Snippets
      </span>
      <select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        disabled={snippets.isLoading || list.length === 0}
        className="min-w-[200px] rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        title={selected?.description || undefined}
      >
        <option value="">
          {snippets.isLoading
            ? 'Loading…'
            : list.length === 0
              ? 'No snippets saved yet'
              : 'Pick a snippet…'}
        </option>
        {list.map((s) => (
          <option key={s.id} value={s.id} title={s.description}>
            {s.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!selected}
        onClick={() => selected && onInsert(selected.content)}
        className="rounded-md border border-input px-2 py-1 text-sm hover:bg-tres-submenu-bg disabled:opacity-40"
      >
        Insert
      </button>
      {selected?.description && (
        <span className="max-w-md truncate text-xs text-tres-text-secondary" title={selected.description}>
          {selected.description}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {canManage && (
          <>
            <button
              type="button"
              onClick={() => setSaveOpen(true)}
              disabled={currentText.trim().length === 0}
              className="rounded-md border border-input px-2 py-1 text-sm hover:bg-tres-submenu-bg disabled:opacity-40"
            >
              Save current…
            </button>
            <button
              type="button"
              onClick={() => selected && setEditing(selected)}
              disabled={!selected}
              className="rounded-md border border-input px-2 py-1 text-sm hover:bg-tres-submenu-bg disabled:opacity-40"
            >
              Edit…
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={!selected || busy}
              className={buttonVariants({ variant: 'danger', size: 'md' })}
            >
              Delete
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="w-full text-xs text-tres-error" role="alert">
          {error}
        </p>
      )}

      {saveOpen && (
        <SaveSnippetModal
          content={currentText}
          onClose={() => setSaveOpen(false)}
          onSaved={() => {
            setSaveOpen(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <SaveSnippetModal
          content={editing.content}
          snippet={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function SaveSnippetModal({
  content,
  snippet,
  onClose,
  onSaved,
}: {
  content: string;
  /** Present = edit an existing snippet (PATCH); absent = create. */
  snippet?: SnippetDef;
  onClose: () => void;
  onSaved: () => void;
}) {
  const auth = useAuth();
  const [name, setName] = useState(snippet?.name ?? '');
  const [description, setDescription] = useState(snippet?.description ?? '');
  const [body, setBody] = useState(content);
  const [state, setState] = useState<{ status: 'idle' | 'saving' | 'error'; message?: string }>({
    status: 'idle',
  });

  const handleSave = async () => {
    const token = auth.user?.id_token;
    if (!token || name.trim().length === 0) return;
    setState({ status: 'saving' });
    const payload = {
      name: name.trim(),
      content: body,
      description: description.trim(),
    };
    const r = snippet
      ? await patchSnippet(snippet.id, payload, token)
      : await createSnippet(payload, token);
    if (r.ok) {
      onSaved();
    } else {
      setState({ status: 'error', message: r.message });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-lg">
        <h2 className="text-lg font-semibold text-tres-text-primary">
          {snippet ? 'Edit snippet' : 'Save snippet'}
        </h2>
        <label className="mt-3 block">
          <span className="text-sm font-medium">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
          />
        </label>
        <label className="mt-3 block">
          <span className="text-sm font-medium">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
          />
        </label>
        <label className="mt-3 block">
          <span className="text-sm font-medium">Query</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            spellCheck={false}
            className="mt-1 w-full rounded-md border border-border px-3 py-2 font-mono text-xs focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
          />
        </label>
        {state.status === 'error' && (
          <div className={cn(messageClass('warning', 'normal'), 'mt-3')}>
            {state.message}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={state.status === 'saving'}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={state.status === 'saving' || name.trim().length === 0}
            className={cn(
              'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground',
              'hover:bg-primary/90 disabled:opacity-50',
            )}
          >
            {state.status === 'saving' ? 'Saving…' : snippet ? 'Save changes' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
